import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ScheduleDetail {
  id: number
  title: string | null
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_name: string | null
  notes: string | null
  team: string[]
  added_by: string | null
  added_on: string | null
  date_description: string | null
}

interface Props {
  case_record: CaseRecord
  schedule: ScheduleDetail
  can_edit: boolean
  can_delete: boolean
}

export default function Show({
  case_record,
  schedule,
  can_edit,
  can_delete,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Schedule", href: `/cases/${case_record.id}/schedules` },
    {
      title: "View",
      href: `/cases/${case_record.id}/schedules/${schedule.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/schedules`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this schedule entry?")) {
      router.delete(`${basePath}/${schedule.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Schedule - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Scheduled Work</h1>
          <div className="flex gap-2">
            {can_edit && (
              <Link
                href={`${basePath}/${schedule.id}/edit`}
                className="text-hpi-active-blue hover:underline"
              >
                Edit
              </Link>
            )}
            {can_delete && (
              <button
                onClick={handleDelete}
                className="text-hpi-red hover:underline"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="border-hpi-light-gray max-w-3xl rounded border p-6">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-hpi-med-gray text-sm font-medium">
                Start
              </label>
              <div>
                {schedule.no_start
                  ? `${schedule.start_date?.split(" ")[0] ?? ""} TBD`
                  : (schedule.start_date ?? "—")}
              </div>
            </div>
            {!schedule.no_end && schedule.end_date && (
              <div>
                <label className="text-hpi-med-gray text-sm font-medium">
                  End
                </label>
                <div>{schedule.end_date}</div>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="text-hpi-med-gray text-sm font-medium">
              Team
            </label>
            <div>
              {schedule.leader_name ? (
                <span className="font-semibold">{schedule.leader_name}</span>
              ) : (
                <span className="text-hpi-red font-semibold">
                  Leader UNASSIGNED
                </span>
              )}
              {schedule.team.length > 0 && (
                <span>, {schedule.team.join(", ")}</span>
              )}
            </div>
          </div>

          {schedule.title && (
            <div className="mb-4">
              <label className="text-hpi-med-gray text-sm font-medium">
                Title
              </label>
              <div className="font-semibold">{schedule.title}</div>
            </div>
          )}

          {schedule.notes && (
            <div className="mb-4">
              <label className="text-hpi-med-gray text-sm font-medium">
                Description
              </label>
              <div className="text-sm whitespace-pre-line">
                {schedule.notes}
              </div>
            </div>
          )}

          {schedule.added_by && (
            <div className="text-hpi-med-gray mt-4 text-xs">
              Added on {schedule.added_on} by {schedule.added_by}
            </div>
          )}
        </div>

        <div className="mt-4">
          <Link
            href={basePath}
            className="text-hpi-active-blue hover:underline"
          >
            Return to Schedule List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
